import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions, UseQueryResult, QueryKey } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

export interface MyStatementMatchedReceipt {
  id: number;
  vendor: string | null;
  originalFilename: string;
}

export interface MyStatementEntry {
  id: number;
  transactionDate: string;
  description: string;
  amount: number;
  matched: boolean;
  noReceipt: boolean;
  matchedReceipt: MyStatementMatchedReceipt | null;
}

export interface MyStatement {
  id: number;
  originalFilename: string;
  statementDate: string | null;
  companyReference: string | null;
  totalSpending: number | null;
  totalEntries: number;
  matchedEntries: number;
  noReceiptEntries: number;
  entries: MyStatementEntry[];
}

export const getMyStatementsQueryKey = () => ["/api/statements/mine"] as const;

export const getMyStatements = async (options?: RequestInit): Promise<MyStatement[]> => {
  return customFetch<MyStatement[]>("/api/statements/mine", { ...options, method: "GET" });
};

export function useMyStatements<
  TData = MyStatement[],
  TError = unknown,
>(options?: {
  query?: UseQueryOptions<MyStatement[], TError, TData>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryKey = options?.query?.queryKey ?? getMyStatementsQueryKey();
  const queryFn = ({ signal }: { signal?: AbortSignal }) =>
    getMyStatements({ signal });

  const query = useQuery({
    queryKey,
    queryFn,
    ...options?.query,
  }) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return { ...query, queryKey };
}
